import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import DBWorkoutCanvas from "./DBWorkoutCanvas";
import { DIFFICULTY, type Difficulty } from "@/integrations/backend/enums";
import {
  addExerciseToTemplate,
  combineExercises,
  extractExerciseToNewGrouping,
  fetchTemplateGroupings,
  reorderExercisesWithinGrouping,
  reorderGroupings,
  updateExerciseFields,
  updateGroupingRest,
  updateGroupingSets,
  moveExerciseToGroupingEnd,
  deleteExerciseAndCleanup,
  type UIExercise,
  type UIGrouping,
} from "@/integrations/backend/workouts";
import {
  addExerciseToAssignedWorkout,
  combineAssignedExercises,
  extractAssignedExerciseToNewGrouping,
  fetchAssignedWorkoutGroupings,
  reorderAssignedExercises,
  reorderAssignedGroupings,
  updateAssignedExerciseFields,
  updateAssignedGroupingRest,
  updateAssignedGroupingSets,
  moveAssignedExerciseToGroupingEnd,
  deleteAssignedExerciseAndCleanup,
  getAssignedWorkoutMeta,
  patchAssignedWorkoutMeta,
} from "@/integrations/backend/workout-assigned";
import { toast } from "sonner";
import { getTemplateMeta, patchTemplateMeta } from "@/integrations/backend/workout-templates";
import { loadExercisesWithRelations } from "@/integrations/backend/exercises";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { listClients, type AdminClient } from "@/integrations/backend/admin-users";
import DBExercisePickerDialog from "./DBExercisePickerDialog";
import { parseYYYYMMDDToLocalDate } from "@/lib/date-only";
import BuilderShell from "./builder/BuilderShell";
import DBWorkoutBuilderHeader from "./db-builder/DBWorkoutBuilderHeader";
import DBWorkoutBuilderPanels from "./db-builder/DBWorkoutBuilderPanels";
import { queryKeys } from "@/lib/query-keys";

interface DBWorkoutBuilderProps {
  templateId?: string;
  assignedWorkoutId?: string;
  onClose: () => void;
  workoutMode?: "template" | "assigned";
  clientId?: string | null;
  assignmentDate?: Date | null;
  backContext?: "templates" | "client";
  backToClientId?: string;
  onEditTemplate?: (templateId: string) => void;
}

export default function DBWorkoutBuilder({ 
  templateId,
  assignedWorkoutId,
  onClose,
  workoutMode: initialWorkoutMode = "template",
  clientId: initialClientId = null,
  assignmentDate: initialAssignmentDate = null,
  backContext = "templates",
  backToClientId,
  onEditTemplate
}: DBWorkoutBuilderProps) {
  // Determine if we're in assigned mode based on assignedWorkoutId presence
  const isAssignedMode = !!assignedWorkoutId;
  const effectiveWorkoutMode = isAssignedMode ? "assigned" : initialWorkoutMode;
  const [name, setName] = useState<string>("");
  const [duration, setDuration] = useState<number>(30);
  const [groupings, setGroupings] = useState<UIGrouping[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [exerciseNames, setExerciseNames] = useState<Record<string, string>>({});
  const [activeDifficulties, setActiveDifficulties] = useState<Set<Difficulty>>(
    () => new Set(DIFFICULTY as unknown as Difficulty[])
  );
  const [workoutMode, setWorkoutMode] = useState<"template" | "assigned">(effectiveWorkoutMode);
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [savingOps, setSavingOps] = useState(0);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(initialClientId);
  const [assignmentDate, setAssignmentDate] = useState<Date | null>(
    initialAssignmentDate || (effectiveWorkoutMode === "assigned" ? new Date() : null)
  );
  const [workoutTemplateId, setWorkoutTemplateId] = useState<string | null>(null);
  const [allClients, setAllClients] = useState<AdminClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState<boolean>(false);
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  const invalidateTemplateCache = useCallback(
    (tid: string) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workoutTemplates.list() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workoutTemplates.meta(tid) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.workoutTemplates.groupings(tid) });
    },
    [queryClient]
  );

  const templateSuccessfulLoadCountRef = useRef(0);

  const beginSaving = useCallback(() => setSavingOps((c) => c + 1), []);
  const endSaving = useCallback((success: boolean) => {
    setSavingOps((c) => Math.max(0, c - 1));
    if (success) setLastSavedAt(new Date());
  }, []);
  const isSaving = savingOps > 0;

  // Update selected client and date when props change
  useEffect(() => {
    setSelectedClientId(initialClientId);
  }, [initialClientId]);

  useEffect(() => {
    if (initialAssignmentDate !== null) {
      setAssignmentDate(initialAssignmentDate);
    } else if (initialAssignmentDate === null && workoutMode === "assigned") {
      // Default to today if no date provided in assigned mode
      setAssignmentDate(new Date());
    }
  }, [initialAssignmentDate, workoutMode]);

  // Fetch clients on mount
  useEffect(() => {
    if (!isAssignedMode) return;
    
    const fetchClients = async () => {
      try {
        setClientsLoading(true);
        const clients = await listClients();
        setAllClients(clients);
      } catch (error: any) {
        console.error("Failed to fetch clients", error);
        toast.error("Failed to load clients", { description: error?.message ?? String(error) });
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
  }, [isAssignedMode]);

  // Get client list for dropdown - filter clients assigned to current admin user
  const clientOptions = useMemo(() => {
    if (!profile?.id) return [];
    
    // Filter clients assigned to the current admin user
    const assignedClients = allClients.filter(
      client => client.trainer?.id === profile.id
    );
    
    // If there's a selected client that's not in the assigned list, include it
    const currentSelectedClient = selectedClientId 
      ? allClients.find(c => c.id === selectedClientId)
      : null;
    
    const options = [...assignedClients];
    
    // Add selected client if it exists and isn't already in the list
    if (currentSelectedClient && !assignedClients.find(c => c.id === currentSelectedClient.id)) {
      options.unshift(currentSelectedClient);
    }
    
    return options.map(client => ({
      id: client.id,
      name: `${client.first_name || ""} ${client.last_name || ""}`.trim() || "Unnamed Client",
      email: client.email || ""
    }));
  }, [allClients, profile?.id, selectedClientId]);

  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    if (backContext === "client" && backToClientId) {
      // Navigate back to client profile - this will be handled by AdminPortal's state
      onClose();
    } else {
      onClose();
    }
  }, [backContext, backToClientId, onClose]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    let ok = false;
    try {
      if (isAssignedMode && assignedWorkoutId) {
        const t = await getAssignedWorkoutMeta(assignedWorkoutId);
        setName((t.name as string) ?? "Untitled Workout");
        setDuration((t.duration_minutes as number) ?? 30);
        setWorkoutTemplateId(t.workout_template_id);
        // Load date from backend if available, otherwise use today
        if (t.date) {
          setAssignmentDate(parseYYYYMMDDToLocalDate(t.date));
        } else {
          setAssignmentDate(new Date());
        }
        const gs = await fetchAssignedWorkoutGroupings(assignedWorkoutId);
        setGroupings(gs);
        const ids = new Set<string>();
        gs.forEach(g => g.exercises.forEach(e => ids.add(e.exercise)));
        if (ids.size > 0) {
          const all = await loadExercisesWithRelations();
          const map: Record<string,string> = {};
          all.forEach((r) => { if (ids.has(r.id)) map[r.id] = r.name; });
          setExerciseNames(map);
        } else {
          setExerciseNames({});
        }
      } else if (templateId) {
        const t = await getTemplateMeta(templateId);
        setName((t.name as string) ?? "Untitled Workout");
        setDuration((t.duration_minutes as number) ?? 30);
        const gs = await fetchTemplateGroupings(templateId);
        setGroupings(gs);
        const ids = new Set<string>();
        gs.forEach(g => g.exercises.forEach(e => ids.add(e.exercise)));
        if (ids.size > 0) {
          const all = await loadExercisesWithRelations();
          const map: Record<string,string> = {};
          all.forEach((r) => { if (ids.has(r.id)) map[r.id] = r.name; });
          setExerciseNames(map);
        } else {
          setExerciseNames({});
        }
      }
      ok = true;
      if (templateId && !isAssignedMode) {
        templateSuccessfulLoadCountRef.current += 1;
        if (templateSuccessfulLoadCountRef.current > 1) {
          invalidateTemplateCache(templateId);
        }
      }
      // On first successful load, initialize the "last saved" indicator so it doesn't show empty.
      setLastSavedAt((prev) => prev ?? new Date());
    } catch (e: any) {
      toast.error("Could not load workout", { description: e?.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, invalidateTemplateCache]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveMeta = useCallback(async (patch: Partial<{ name: string; duration_minutes: number; date: string }>) => {
    beginSaving();
    let success = false;
    if (isAssignedMode && assignedWorkoutId) {
      try {
        await patchAssignedWorkoutMeta(assignedWorkoutId, patch);
        success = true;
      } finally {
        endSaving(success);
      }
    } else if (templateId) {
      try {
        await patchTemplateMeta(templateId, patch);
        success = true;
        invalidateTemplateCache(templateId);
      } finally {
        endSaving(success);
      }
    } else {
      endSaving(false);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, beginSaving, endSaving, invalidateTemplateCache]);

  const handleAddExercise = useCallback(async (exerciseId: string) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode && assignedWorkoutId) {
        await addExerciseToAssignedWorkout(assignedWorkoutId, exerciseId);
      } else if (templateId) {
        await addExerciseToTemplate(templateId, exerciseId);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not add exercise", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, loadAll, beginSaving, endSaving]);

  const handleAddExercises = useCallback(async (exerciseIds: string[]) => {
    if (exerciseIds.length === 0) return;
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode && assignedWorkoutId) {
        for (const id of exerciseIds) {
          await addExerciseToAssignedWorkout(assignedWorkoutId, id);
        }
      } else if (templateId) {
        for (const id of exerciseIds) {
          await addExerciseToTemplate(templateId, id);
        }
      }
      await loadAll();
      toast.success(`Added ${exerciseIds.length} exercise${exerciseIds.length === 1 ? "" : "s"}`);
      success = true;
    } catch (e: any) {
      toast.error("Could not add exercises", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, loadAll, beginSaving, endSaving]);

  const handleUpdateGroupingSets = useCallback(async (groupingId: string, sets: number) => {
    // Optimistic UI update to avoid input flicker while the network request is in-flight.
    setGroupings(prev => prev.map(g => g.id === groupingId ? { ...g, sets } : g));
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await updateAssignedGroupingSets(groupingId, sets);
      } else {
        await updateGroupingSets(groupingId, sets);
      }
      success = true;
    } catch (e: any) {
      await loadAll();
      toast.error("Could not update sets", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll]);

  const handleUpdateGroupingRest = useCallback(async (groupingId: string, restSeconds: number) => {
    // Optimistic UI update to avoid input flicker while the network request is in-flight.
    setGroupings(prev => prev.map(g => g.id === groupingId ? { ...g, rest_seconds: restSeconds } : g));
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await updateAssignedGroupingRest(groupingId, restSeconds);
      } else {
        await updateGroupingRest(groupingId, restSeconds);
      }
      success = true;
    } catch (e: any) {
      await loadAll();
      toast.error("Could not update group rest", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  const handleUpdateExercise = useCallback(async (exerciseId: string, patch: Partial<UIExercise>) => {
    // Optimistic UI update to avoid input flicker while the network request is in-flight.
    setGroupings(prev => prev.map(g => ({ ...g, exercises: g.exercises.map(ex => ex.id === exerciseId ? { ...ex, ...patch } as any : ex) })));
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await updateAssignedExerciseFields(exerciseId, patch as any);
      } else {
        await updateExerciseFields(exerciseId, patch as any);
      }
      success = true;
    } catch (e: any) {
      await loadAll();
      toast.error("Could not update exercise", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  const handleReorderGroupings = useCallback(async (idsInOrder: string[]) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode && assignedWorkoutId) {
        await reorderAssignedGroupings(assignedWorkoutId, idsInOrder);
      } else if (templateId) {
        await reorderGroupings(templateId, idsInOrder);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not reorder groups", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, loadAll, beginSaving, endSaving]);

  const handleReorderExercises = useCallback(async (groupingId: string, exerciseIdsInOrder: string[]) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await reorderAssignedExercises(groupingId, exerciseIdsInOrder);
      } else {
        await reorderExercisesWithinGrouping(groupingId, exerciseIdsInOrder);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not reorder exercises", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  const handleExtractExercise = useCallback(async (exerciseId: string, canvasIndex: number) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode && assignedWorkoutId) {
        await extractAssignedExerciseToNewGrouping(exerciseId, assignedWorkoutId, canvasIndex);
        const after = await fetchAssignedWorkoutGroupings(assignedWorkoutId);
        const ids = after.map(g => g.id);
        await reorderAssignedGroupings(assignedWorkoutId, ids);
      } else if (templateId) {
        await extractExerciseToNewGrouping(exerciseId, templateId, canvasIndex);
        const after = await fetchTemplateGroupings(templateId);
        const ids = after.map(g => g.id);
        await reorderGroupings(templateId, ids);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not extract exercise", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, assignedWorkoutId, templateId, loadAll, beginSaving, endSaving]);

  const handleCombine = useCallback(async (droppedExerciseId: string, targetExerciseId: string) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await combineAssignedExercises(droppedExerciseId, targetExerciseId);
      } else {
        await combineExercises(droppedExerciseId, targetExerciseId);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not combine exercises", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  const handleMoveExerciseToGroupingEnd = useCallback(async (targetGroupingId: string, exerciseId: string) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await moveAssignedExerciseToGroupingEnd(exerciseId, targetGroupingId);
      } else {
        await moveExerciseToGroupingEnd(exerciseId, targetGroupingId);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not move exercise", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  const handleDeleteExercise = useCallback(async (exerciseId: string) => {
    beginSaving();
    let success = false;
    try {
      if (isAssignedMode) {
        await deleteAssignedExerciseAndCleanup(exerciseId);
      } else {
        await deleteExerciseAndCleanup(exerciseId);
      }
      await loadAll();
      success = true;
    } catch (e: any) {
      toast.error("Could not delete exercise", { description: e?.message ?? String(e) });
    } finally {
      endSaving(success);
    }
  }, [isAssignedMode, loadAll, beginSaving, endSaving]);

  return (
    <BuilderShell
      header={
        <DBWorkoutBuilderHeader
          name={name}
          onNameChange={async (next) => {
            setName(next);
            await saveMeta({ name: next });
          }}
          durationMinutes={duration}
          onDurationMinutesChange={async (next) => {
            setDuration(next);
            await saveMeta({ duration_minutes: next });
          }}
          isSaving={isSaving}
          lastSavedAt={lastSavedAt}
          backLabel={backContext === "client" ? "Back to client" : "Back to templates"}
          onBack={handleBack}
          isAssignedMode={isAssignedMode}
          workoutMode={workoutMode}
          onWorkoutModeChange={(v) => setWorkoutMode(v)}
          clientsLoading={clientsLoading}
          selectedClientId={selectedClientId}
          clientOptions={clientOptions}
          assignmentDate={assignmentDate}
          onAssignmentDateChange={async (newDate) => {
            setAssignmentDate(newDate);
            if (isAssignedMode && assignedWorkoutId && newDate) {
              const dateStr = format(newDate, "yyyy-MM-dd");
              await saveMeta({ date: dateStr });
            }
          }}
        />
      }
    >
      <>
        <DBWorkoutBuilderPanels
          onOpenBrowse={() => setExercisePickerOpen(true)}
          canvasHeaderRight={
            <>
              {(DIFFICULTY as unknown as Difficulty[]).map((level) => (
                <Button
                  key={level}
                  variant={activeDifficulties.has(level) ? "default" : "outline"}
                  size="sm"
                  className="rounded-full h-8 px-2 sm:px-3 text-xs sm:text-sm"
                  onClick={() =>
                    setActiveDifficulties((prev) => {
                      const next = new Set(prev);
                      if (next.has(level)) next.delete(level);
                      else next.add(level);
                      return next;
                    })
                  }
                >
                  <span className="sm:hidden">{level.slice(0, 3)}</span>
                  <span className="hidden sm:inline">{level}</span>
                </Button>
              ))}
            </>
          }
          canvas={
            <DBWorkoutCanvas
              templateId={templateId || assignedWorkoutId || ""}
              groupings={groupings}
              exerciseNameById={exerciseNames}
              activeDifficulties={activeDifficulties}
              onUpdateGroupingSets={handleUpdateGroupingSets}
              onUpdateGroupingRest={handleUpdateGroupingRest}
              onUpdateExercise={handleUpdateExercise}
              onReorderGroupings={handleReorderGroupings}
              onReorderExercises={handleReorderExercises}
              onExtractExerciseToIndex={handleExtractExercise}
              onCombineExercises={handleCombine}
              onDeleteExercise={handleDeleteExercise}
              onMoveExerciseToGroupingEnd={handleMoveExerciseToGroupingEnd}
            />
          }
        />

        <DBExercisePickerDialog
          open={exercisePickerOpen}
          onOpenChange={setExercisePickerOpen}
          onAddMany={handleAddExercises}
        />
      </>
    </BuilderShell>
  );
}


